'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  RefObject,
} from 'react';
import {
  PhonicsSound,
  PhonicsLesson,
  PhonicsActivity,
  PhonicsEventMeta,
  useGetPhonicsSoundsQuery,
  useGetPhonicsLessonGroupsWithLessonsQuery,
} from '@/api/types';
import { getLessonByActivityFromGroups } from '@/utils/lessons';
import { CarouselRef } from '@/components';

type LessonPlayerSlides = Record<
  string,
  {
    getActivityResponseMetaData?: () => PhonicsEventMeta;
    onBeforeLeave?: () => boolean;
    onSlide?: () => void;
    onSlideSettle?: () => void;
    progressPercentage?: number;
  }
>;

type ButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  hint?: boolean;
  visible?: boolean;
};

interface LessonPlayerContextData {
  playerReady: boolean;
  sounds: Array<PhonicsSound>;
  getLessonByActivity: (activity: PhonicsActivity) => PhonicsLesson | undefined;
  getEncounteredLetters: (tillLessonId: string) => Array<string>;
  enablePlayButton: (props: ButtonProps) => void;
  disablePlayButton: () => void;
  enableRefreshButton: (props: ButtonProps) => void;
  disableRefreshButton: () => void;
  flushSlides: () => void;
  registerSlide: (
    id: string,
    getActivityResponseMetaData?: () => PhonicsEventMeta,
    onBeforeLeave?: () => boolean,
    onSlide?: () => void,
    onSlideSettle?: () => void,
    progressPercentage?: number,
  ) => void;
  unRegisterSlide: (id: string) => void;
  getActivityResponseMetaData: (id: string) => PhonicsEventMeta | undefined;
  onBeforeLeave: (id: string) => boolean;
  onSlide: (id: string) => void;
  onSlideSettle: (id: string) => void;
  getActivityProgress: (id: string) => number | undefined;
  scrollToSlideIndex?: (index: number) => void;
  scrollToSlideId?: (id: string) => void;
  scrollNextSlide?: () => void;
  carouselRef?: RefObject<CarouselRef | null>;
  playButtonProps?: ButtonProps;
  refreshButtonProps?: ButtonProps;
}

const initialValue: LessonPlayerContextData = {
  playerReady: false,
  sounds: [],
  getLessonByActivity: () => undefined,
  getEncounteredLetters: () => [],
  enablePlayButton: () => undefined,
  disablePlayButton: () => undefined,
  enableRefreshButton: () => undefined,
  disableRefreshButton: () => undefined,
  flushSlides: () => undefined,
  registerSlide: () => undefined,
  unRegisterSlide: () => undefined,
  getActivityResponseMetaData: () => undefined,
  onBeforeLeave: () => true,
  onSlide: () => undefined,
  onSlideSettle: () => undefined,
  getActivityProgress: () => undefined,
  playButtonProps: undefined,
  refreshButtonProps: undefined,
};

const LessonPlayerContext =
  createContext<LessonPlayerContextData>(initialValue);

export const LessonPlayerContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const carouselRef = useRef<CarouselRef>(null);
  const slidesRef = useRef<LessonPlayerSlides>({});

  const { data: soundsResult } = useGetPhonicsSoundsQuery();

  const { data: lessonGroupsResult } =
    useGetPhonicsLessonGroupsWithLessonsQuery();

  const playerReady = useMemo(
    () => !!soundsResult && !!lessonGroupsResult,
    [soundsResult, lessonGroupsResult],
  );

  const [playButtonProps, setPlayButtonProps] = useState<
    ButtonProps | undefined
  >(undefined);

  const [refreshButtonProps, setRefreshButtonProps] = useState<
    ButtonProps | undefined
  >(undefined);

  const getLessonByActivity = useCallback(
    (activity: PhonicsActivity) =>
      getLessonByActivityFromGroups(
        activity,
        lessonGroupsResult?.getPhonicsLessonGroups || [],
      ),
    [lessonGroupsResult],
  );

  const getEncounteredLetters = useCallback(
    (tillLessonId: string) => {
      const letters = new Set<string>();
      let shouldStop = false;

      lessonGroupsResult?.getPhonicsLessonGroups.forEach((yearGroup) => {
        if (shouldStop) {
          return;
        }

        yearGroup.children?.forEach((weekGroup) => {
          if (shouldStop) {
            return;
          }

          weekGroup.lessons?.forEach((lesson) => {
            if (shouldStop) {
              return;
            }

            lesson.activities.forEach((activity) => {
              activity.words?.forEach((word) => {
                for (const letter of word.toLowerCase()) {
                  letters.add(letter);
                }
              });

              activity.words2?.forEach((word) => {
                for (const letter of word.toLowerCase()) {
                  letters.add(letter);
                }
              });
            });

            if (lesson.id === tillLessonId) {
              shouldStop = true;
            }
          });
        });
      });

      return Array.from(letters);
    },
    [lessonGroupsResult],
  );

  const enablePlayButton = useCallback((props: ButtonProps) => {
    setPlayButtonProps({
      ...props,
      visible: true,
    });
  }, []);

  const disablePlayButton = useCallback(() => {
    setPlayButtonProps((prevProps) =>
      prevProps
        ? {
            ...prevProps,
            visible: false,
          }
        : undefined,
    );
  }, []);

  const enableRefreshButton = useCallback((props: ButtonProps) => {
    setRefreshButtonProps({
      ...props,
      visible: true,
    });
  }, []);

  const disableRefreshButton = useCallback(() => {
    setRefreshButtonProps((prevProps) =>
      prevProps
        ? {
            ...prevProps,
            visible: false,
          }
        : undefined,
    );
  }, []);

  const getSlideById = useCallback((id: string) => {
    return slidesRef.current?.[id];
  }, []);

  const getSlideIndexById = useCallback((id: string) => {
    return Object.keys(slidesRef.current).findIndex((key) => key === id);
  }, []);

  const flushSlides = useCallback(() => {
    slidesRef.current = {};
  }, []);

  const registerSlide = useCallback(
    async (
      id: string,
      getActivityResponseMetaData?: () => PhonicsEventMeta,
      onBeforeLeave?: () => boolean,
      onSlide?: () => void,
      onSlideSettle?: () => void,
      progressPercentage?: number,
    ) => {
      slidesRef.current[id] = {
        getActivityResponseMetaData,
        onBeforeLeave,
        onSlide,
        onSlideSettle,
        progressPercentage,
      };
    },
    [getSlideById],
  );

  const unRegisterSlide = useCallback((id: string) => {
    delete slidesRef.current[id];
  }, []);

  const getActivityResponseMetaData = useCallback(
    (id: string) => {
      const slide = getSlideById(id);

      if (typeof slide?.getActivityResponseMetaData === 'function') {
        return slide.getActivityResponseMetaData();
      }

      return undefined;
    },
    [getSlideById],
  );

  const onBeforeLeave = useCallback(
    (id: string) => {
      const slide = getSlideById(id);

      if (typeof slide?.onBeforeLeave === 'function') {
        return slide.onBeforeLeave();
      }

      return true;
    },
    [getSlideById],
  );

  const onSlide = useCallback(
    (id: string) => {
      const slide = getSlideById(id);

      if (typeof slide?.onSlide === 'function') {
        slide.onSlide();
      }
    },
    [getSlideById],
  );

  const onSlideSettle = useCallback(
    (id: string) => {
      const slide = getSlideById(id);

      if (typeof slide?.onSlideSettle === 'function') {
        slide.onSlideSettle();
      }
    },
    [getSlideById],
  );

  const getActivityProgress = useCallback(
    (id: string) => {
      const slide = getSlideById(id);

      return slide?.progressPercentage;
    },
    [getSlideById],
  );

  const scrollToSlideIndex = useCallback(
    (index: number) => {
      carouselRef.current?.scrollTo(index);
    },
    [carouselRef],
  );

  const scrollToSlideId = useCallback(
    (id: string) => {
      scrollToSlideIndex(getSlideIndexById(id));
    },
    [getSlideIndexById, scrollToSlideIndex],
  );

  const scrollNextSlide = useCallback(() => {
    carouselRef.current?.scrollNext();
  }, [carouselRef]);

  const contextValue: LessonPlayerContextData = useMemo(
    () => ({
      playerReady,
      sounds: soundsResult?.getPhonicsSounds || [],
      getLessonByActivity,
      getEncounteredLetters,
      enablePlayButton,
      disablePlayButton,
      enableRefreshButton,
      disableRefreshButton,
      flushSlides,
      registerSlide,
      unRegisterSlide,
      getActivityResponseMetaData,
      onBeforeLeave,
      onSlide,
      onSlideSettle,
      getActivityProgress,
      scrollToSlideIndex,
      scrollToSlideId,
      scrollNextSlide,
      carouselRef,
      playButtonProps,
      refreshButtonProps,
    }),
    [
      playerReady,
      soundsResult,
      getLessonByActivity,
      getEncounteredLetters,
      enablePlayButton,
      disablePlayButton,
      enableRefreshButton,
      disableRefreshButton,
      flushSlides,
      registerSlide,
      unRegisterSlide,
      getActivityResponseMetaData,
      onBeforeLeave,
      onSlide,
      onSlideSettle,
      getActivityProgress,
      scrollToSlideIndex,
      scrollToSlideId,
      scrollNextSlide,
      carouselRef,
      playButtonProps,
      refreshButtonProps,
    ],
  );

  return (
    <LessonPlayerContext.Provider value={contextValue}>
      {children}
    </LessonPlayerContext.Provider>
  );
};

export const useLessonPlayer = () => useContext(LessonPlayerContext);
export const useSounds = () => useLessonPlayer().sounds;
